import { formatDate } from "./format";

/**
 * Agent 话题（原型 Wiki A-02：新建话题 / 切换话题 / 话题列表）。
 *
 * 后端契约（`API-契约-M2.md` §2）目前是**一个项目一个会话**：
 * `GET .../agent/view` 只回一个 `sessionId`，没有「列会话 / 建会话 / 删会话」。
 * 所以这一版话题表是**本地 state**（按项目存 localStorage），每个话题记一个
 * `sessionId`：默认话题绑定后端真实会话，新建话题先留空，等后端补接口后
 * 换成 `GET/POST .../agent/sessions`（见交付报告里的「需要的接口」）。
 *
 * 这里只放纯函数，方便单测；组件只负责渲染与持久化调用。
 */

export interface AgentTopic {
  id: string;
  title: string;
  /** 后端会话 id；新话题在后端支持多会话前是 null。 */
  sessionId: string | null;
  createdAt: number;
  /** 最后活动时间：列表按它倒序（原型「标题 + 最后活动时间」）。 */
  lastActiveAt: number;
}

export const TOPIC_STORAGE_PREFIX = "dp.agent.topics.";
export const ACTIVE_TOPIC_STORAGE_PREFIX = "dp.agent.active-topic.";
export const DEFAULT_TOPIC_TITLE = "新话题";
const MAX_TITLE = 24;

/** 话题标题：优先用用户改过的名字，其次第一条用户消息，最后兜底「新话题」。 */
export function topicTitleFromText(text: string): string {
  const clean = text.replace(/\s+/g, " ").trim();
  if (clean.length === 0) return DEFAULT_TOPIC_TITLE;
  return clean.length > MAX_TITLE ? `${clean.slice(0, MAX_TITLE)}…` : clean;
}

let seq = 0;

/** 本地话题 id：`tp_<时间>_<序号>`，不依赖 crypto（jsdom 里也稳）。 */
export function newTopicId(now = Date.now()): string {
  seq += 1;
  return `tp_${now.toString(36)}_${seq.toString(36)}`;
}

export function createTopic(options: {
  now?: number;
  title?: string;
  sessionId?: string | null;
} = {}): AgentTopic {
  const now = options.now ?? Date.now();
  return {
    id: newTopicId(now),
    title: options.title ?? DEFAULT_TOPIC_TITLE,
    sessionId: options.sessionId ?? null,
    createdAt: now,
    lastActiveAt: now,
  };
}

/** 列表顺序：最近活动的排前面（同时间保持创建顺序稳定）。 */
export function sortTopics(topics: readonly AgentTopic[]): AgentTopic[] {
  return [...topics].sort((a, b) => {
    if (b.lastActiveAt !== a.lastActiveAt) return b.lastActiveAt - a.lastActiveAt;
    return a.createdAt - b.createdAt;
  });
}

/** 当前话题：找不到就退回最近活动的一个；空列表返回 null。 */
export function resolveActiveTopic(
  topics: readonly AgentTopic[],
  activeId: string | null | undefined,
): AgentTopic | null {
  if (topics.length === 0) return null;
  const hit = topics.find((topic) => topic.id === activeId);
  return hit ?? sortTopics(topics)[0] ?? null;
}

/** 重命名：空标题不生效（避免话题列表出现无字行）。 */
export function renameTopic(
  topics: readonly AgentTopic[],
  id: string,
  title: string,
): AgentTopic[] {
  const next = title.trim().slice(0, MAX_TITLE);
  if (next.length === 0) return [...topics];
  return topics.map((topic) => (topic.id === id ? { ...topic, title: next } : topic));
}

/** 删除；删光了由调用方补一个新话题（这里不做隐式副作用）。 */
export function removeTopic(topics: readonly AgentTopic[], id: string): AgentTopic[] {
  return topics.filter((topic) => topic.id !== id);
}

/** 打一次活动时间戳（发消息 / 切换话题都算活动）。 */
export function touchTopic(
  topics: readonly AgentTopic[],
  id: string,
  at = Date.now(),
): AgentTopic[] {
  return topics.map((topic) => (topic.id === id ? { ...topic, lastActiveAt: at } : topic));
}

/** 绑定后端会话 id（后端多会话后由接口返回，现在只用于默认话题）。 */
export function bindTopicSession(
  topics: readonly AgentTopic[],
  id: string,
  sessionId: string,
): AgentTopic[] {
  return topics.map((topic) =>
    topic.id === id && topic.sessionId !== sessionId ? { ...topic, sessionId } : topic,
  );
}

function readJson<T>(storage: Storage | null, key: string): T | null {
  if (!storage) return null;
  try {
    const raw = storage.getItem(key);
    if (!raw) return null;
    return JSON.parse(raw) as T;
  } catch {
    return null;
  }
}

function isTopic(value: unknown): value is AgentTopic {
  if (typeof value !== "object" || value === null) return false;
  const item = value as Record<string, unknown>;
  return (
    typeof item.id === "string" &&
    typeof item.title === "string" &&
    (item.sessionId === null || typeof item.sessionId === "string") &&
    typeof item.createdAt === "number" &&
    typeof item.lastActiveAt === "number"
  );
}

/** 读本地话题表；坏数据（旧版本 / 手改过）当没有，不让面板崩。 */
export function loadTopics(
  projectId: string,
  storage: Storage | null = safeStorage(),
): AgentTopic[] {
  const parsed = readJson<unknown>(storage, `${TOPIC_STORAGE_PREFIX}${projectId}`);
  if (!Array.isArray(parsed)) return [];
  return parsed.filter(isTopic);
}

export function saveTopics(
  projectId: string,
  topics: readonly AgentTopic[],
  storage: Storage | null = safeStorage(),
): void {
  if (!storage) return;
  try {
    storage.setItem(`${TOPIC_STORAGE_PREFIX}${projectId}`, JSON.stringify(topics));
  } catch {
    // 配额满 / 隐私模式：话题表退化成内存态，不影响对话。
  }
}

export function loadActiveTopicId(
  projectId: string,
  storage: Storage | null = safeStorage(),
): string | null {
  if (!storage) return null;
  try {
    return storage.getItem(`${ACTIVE_TOPIC_STORAGE_PREFIX}${projectId}`);
  } catch {
    return null;
  }
}

export function saveActiveTopicId(
  projectId: string,
  id: string,
  storage: Storage | null = safeStorage(),
): void {
  if (!storage) return;
  try {
    storage.setItem(`${ACTIVE_TOPIC_STORAGE_PREFIX}${projectId}`, id);
  } catch {
    // 同上：失败不影响当前会话。
  }
}

/** localStorage 可能不存在（SSR / 隐私模式 / jsdom 未给）。 */
export function safeStorage(): Storage | null {
  try {
    if (typeof window === "undefined" || !window.localStorage) return null;
    return window.localStorage;
  } catch {
    return null;
  }
}

/** 话题列表的「最后活动」：刚刚 / N 分钟前 / N 小时前 / 日期。 */
export function formatTopicTime(at: number, now = Date.now()): string {
  if (!Number.isFinite(at)) return "—";
  const diff = Math.max(0, now - at);
  if (diff < 60_000) return "刚刚";
  if (diff < 3_600_000) return `${Math.floor(diff / 60_000)} 分钟前`;
  if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)} 小时前`;
  return formatDate(at);
}
